import {
  HasuraDataTableColumnDef,
  DataTableEx,
  DataTableExProps,
  DataTableExRef,
  AutocompleteInput,
  ConfigurationContext,
} from '@kirz/mui-admin';
import React, {
  forwardRef,
  Ref,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

export const CategoryTemplatesTable = forwardRef(
  (
    props: Partial<DataTableExProps> & {
      id: string;
      categoryId: number;
      categoryType: string;
      filterColumns?: (
        columns: HasuraDataTableColumnDef[],
      ) => HasuraDataTableColumnDef[];
    },
    ref: Ref<DataTableExRef>,
  ) => {
    const { categoryId, filterColumns, ...rest } = props;
    const tableRef = useRef<DataTableExRef>(null);
    const [sortValues, setSortValues] = useState<number[] | null>(null);
    const { hasura } = useContext(ConfigurationContext);

    useImperativeHandle(ref, () => tableRef.current!, []);

    const refreshSortValues = async () => {
      const items = await hasura.request({
        type: 'query',
        source: 'categoryTemplate',
        selection: 'sort',
        where: { categoryId: { _eq: categoryId } },
      });

      setSortValues(items.map((x: any) => x.sort));
    };

    useEffect(() => {
      refreshSortValues();
    }, [categoryId]);

    const columns = useMemo<HasuraDataTableColumnDef[]>(
      () =>
        (filterColumns || ((x: HasuraDataTableColumnDef[]) => x))([
          {
            width: 60,
            field: 'sort',
            type: 'sort',
            sortKey: 'sort',
            rowsSortValues: sortValues,
            onSortChange: async (direction, row, targetRowSort) => {
              const source = 'CategoryTemplate';
              const { id } = row;

              await hasura.request({
                type: 'custom',
                query: `
                  mutation ChangeSort($where1: ${source}BoolExp!, $set1: ${source}SetInput!, $where2: ${source}BoolExp!, $set2: ${source}SetInput!) {
                    result2: update${source}(where: $where2, _set: $set2) {
                      affected_rows
                    }
                    result1: update${source}(where: $where1, _set: $set1) {
                      affected_rows
                    }
                  }
                `,
                variables: {
                  where1: { id: { _eq: id }, categoryId: { _eq: categoryId } },
                  set1: { sort: targetRowSort },
                  where2: {
                    sort: { _eq: targetRowSort },
                    categoryId: { _eq: categoryId },
                  },
                  set2: { sort: row.sort },
                },
              });

              tableRef.current?.reload();
            },
          },
          {
            field: 'template',
            selector: 'template { *name* }',
            headerName: 'Шаблон',
            flex: 1,
            type: 'relationship',
          },
          { field: 'createdAt', headerName: 'Дата создания', type: 'date' },
        ]),
      [filterColumns, sortValues],
    );

    return (
      <DataTableEx
        {...rest}
        ref={tableRef}
        source="categoryTemplate"
        columns={columns}
        persistStateMode="query"
        sortBy={rest.sortBy ?? { field: 'sort', sort: 'asc' }}
        selectProps={{
          filter: {
            categoryId: { _eq: categoryId },
            ...props.selectProps?.filter,
          },
        }}
        formTitle={(isNew) =>
          isNew ? 'Новый шаблон категории' : 'Редактировать шаблон категории'
        }
        formDialogProps={{
          ...rest.formDialogProps,
          maxWidth: 'xs',
          formSubmitterProps: {
            preSubmit(item) {
              return {
                ...item,
                categoryId,
              };
            },
            onSubmit(item) {
              refreshSortValues();
            },
          },
        }}
        deletable={{
          onDeleted: (row) => {
            setSortValues(sortValues!.filter((x) => x !== row.sort));
          },
        }}
      >
        <AutocompleteInput
          name="templateId"
          source="template"
          mode="hasura"
          selection="id name"
          label="Шаблон"
          itemText="name"
          itemValue="id"
          required
          filter={{
            type: { _eq: props.categoryType },
          }}
        />
      </DataTableEx>
    );
  },
);
